/** Data access for maintenance authoring: job templates and plans. */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api, type Schemas } from "@/api/client";
import { unwrap } from "@/api/problem";
import { collectPages, compactParams, type Paged, usePagedQuery } from "@/shared/lib/paging";

export type JobTemplate = Schemas["JobTemplateOut"];
export type JobTemplateIn = Schemas["JobTemplateIn"];
export type JobTemplatePatch = Schemas["JobTemplatePatch"];
export type JobTaskIn = Schemas["JobTaskIn"];
export type ChecklistItemIn = Schemas["ChecklistItemIn"];
export type JobPartIn = Schemas["JobPartIn"];
export type Plan = Schemas["PlanOut"];
export type PlanIn = Schemas["PlanIn"];
export type PlanPatch = Schemas["PlanPatch"];
export type TriggerIn = Schemas["TriggerIn"];
export type AssignmentIn = Schemas["AssignmentIn"];
export type TargetPreview = Schemas["TargetPreviewOut"];

export const TEMPLATE_CATEGORIES = [
  "service",
  "inspection",
  "overhaul",
  "survey",
  "cleaning",
  "repair",
  "other",
] as const;
export const TRIGGER_KINDS = ["calendar", "meter", "event", "condition"] as const;
/** Asset lifecycle events a plan can react to. */
export const EVENT_TRIGGER_TYPES = [
  "asset.created",
  "asset.installed",
  "asset.removed",
  "asset.status_changed",
] as const;

function invalidate(qc: ReturnType<typeof useQueryClient>, ...keys: string[]): void {
  for (const key of keys) {
    void qc.invalidateQueries({ queryKey: [key] });
  }
}

/** Job templates (paged). */
export function useJobTemplates(filters: {
  q?: string;
  category?: string;
  include_inactive?: boolean;
}): Paged<JobTemplate> {
  return usePagedQuery(["job-templates", filters], async (cursor) =>
    unwrap(
      await api.GET("/api/v1/maintenance/job-templates", {
        params: { query: compactParams({ ...filters, cursor, limit: 50 }) },
      }),
    ),
  );
}

/** One job template. */
export function useJobTemplate(id: string | undefined): UseQueryResult<JobTemplate> {
  return useQuery({
    queryKey: ["job-template", id],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/maintenance/job-templates/{template_id}", {
          params: { path: { template_id: id ?? "" } },
        }),
      ),
    enabled: !!id,
  });
}

/** Create or edit a job template. */
export function useSaveJobTemplate(): UseMutationResult<
  JobTemplate,
  Error,
  { id?: string; body: JobTemplateIn | JobTemplatePatch }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }) =>
      id
        ? unwrap(
            await api.PATCH("/api/v1/maintenance/job-templates/{template_id}", {
              params: { path: { template_id: id } },
              body: body,
            }),
          )
        : unwrap(
            await api.POST("/api/v1/maintenance/job-templates", { body: body as JobTemplateIn }),
          ),
    onSuccess: () => invalidate(qc, "job-templates", "job-template", "picker"),
  });
}

/** Plans. */
export function usePlans(): UseQueryResult<Plan[]> {
  return useQuery({
    queryKey: ["plans"],
    queryFn: () =>
      collectPages(async (cursor) =>
        unwrap(
          await api.GET("/api/v1/maintenance/plans", {
            params: { query: compactParams({ cursor, limit: 200 }) },
          }),
        ),
      ),
  });
}

/** Create or edit a plan. */
export function useSavePlan(): UseMutationResult<
  Plan,
  Error,
  { id?: string; body: PlanIn | PlanPatch }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }) =>
      id
        ? unwrap(
            await api.PATCH("/api/v1/maintenance/plans/{plan_id}", {
              params: { path: { plan_id: id } },
              body: body,
            }),
          )
        : unwrap(await api.POST("/api/v1/maintenance/plans", { body: body as PlanIn })),
    onSuccess: () => invalidate(qc, "plans", "schedules", "dashboard"),
  });
}

/** Preview which assets a set of assignments targets. */
export function usePreviewTargets(assignments: AssignmentIn[]): UseQueryResult<TargetPreview> {
  const valid = assignments.filter((a) => Boolean(a.asset_id) || Boolean(a.asset_type_id));
  return useQuery({
    queryKey: ["plan-targets", valid],
    queryFn: async () =>
      unwrap(await api.POST("/api/v1/maintenance/plans/preview-targets", { body: valid })),
    enabled: valid.length > 0,
    staleTime: 10_000,
  });
}

/** Rebase a schedule to a new due point. */
export function useRebaseSchedule(): UseMutationResult<
  Schemas["ScheduleOut"],
  Error,
  { id: string; body: Schemas["RebaseIn"] }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }) =>
      unwrap(
        await api.POST("/api/v1/maintenance/schedules/{schedule_id}/rebase", {
          params: { path: { schedule_id: id } },
          body,
        }),
      ),
    onSuccess: () => invalidate(qc, "schedules", "dashboard"),
  });
}

/** Generate due work orders now. */
export function useGenerateWorkOrders(): UseMutationResult<Schemas["WorkOrderOut"][], Error, void> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => unwrap(await api.POST("/api/v1/work-orders/generate")),
    onSuccess: () => invalidate(qc, "schedules", "work-orders", "dashboard"),
  });
}
